import { RSAA } from 'redux-api-middleware';
import type { IConfigObj } from '@makemydeal/dr-common-utils';
import { buildFromConfig } from '@makemydeal/dr-common-utils';
import type { RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import type { DealerLogoMetadata } from '@makemydeal/dr-dash-types';
import {
    UPLOAD_LOGO,
    GET_PRESIGNED_UPLOAD_URL_REQUEST,
    GET_PRESIGNED_UPLOAD_URL_SUCCESS,
    GET_PRESIGNED_UPLOAD_URL_FAILURE,
    CONFIRM_LOGO_UPLOAD_REQUEST,
    CONFIRM_LOGO_UPLOAD_SUCCESS,
    CONFIRM_LOGO_UPLOAD_FAILURE,
    DELETE_PRINTING_LOGO_REQUEST,
    DELETE_PRINTING_LOGO_SUCCESS,
    DELETE_PRINTING_LOGO_FAILURE,
    FETCH_PRINTING_LOGO_REQUEST,
    FETCH_PRINTING_LOGO_SUCCESS,
    FETCH_PRINTING_LOGO_FAILURE,
    SET_PRINTING_LOGO_CLIENT_ERROR,
    CLEAR_PRINTING_LOGO_ERROR
} from '../actionTypes/globalPrintingSettingsActionTypes';

export const uploadLogo = (file: File, dealerId: number, metadata: DealerLogoMetadata) => ({
    type: UPLOAD_LOGO,
    payload: { file, dealerId, metadata }
});
export const setLogoClientError = (message: string) => ({ type: SET_PRINTING_LOGO_CLIENT_ERROR, payload: { message } });
export const clearPrintingLogoError = () => ({ type: CLEAR_PRINTING_LOGO_ERROR });
export const getPresignedUploadUrl = (dealerId: number, fileType: string, bffEndpointConfig: IConfigObj) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const endpoint = `${proxyUrl}/printing/logo/${encodeURIComponent(dealerId)}/presigned-upload-url`;
    return {
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mimeType: fileType }),
            types: [GET_PRESIGNED_UPLOAD_URL_REQUEST, GET_PRESIGNED_UPLOAD_URL_SUCCESS, GET_PRESIGNED_UPLOAD_URL_FAILURE]
        } satisfies RSAACallRequest
    };
};
export const confirmLogoUpload = (dealerId: number, metadata: DealerLogoMetadata, bffEndpointConfig: IConfigObj) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const endpoint = `${proxyUrl}/printing/logo/${encodeURIComponent(dealerId)}/confirm-upload`;
    return {
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ logoMetadata: metadata }),
            types: [CONFIRM_LOGO_UPLOAD_REQUEST, CONFIRM_LOGO_UPLOAD_SUCCESS, CONFIRM_LOGO_UPLOAD_FAILURE]
        } satisfies RSAACallRequest
    };
};
export const confirmLogoUploadFailure = (message: string) => ({
    type: CONFIRM_LOGO_UPLOAD_FAILURE,
    payload: { message }
});
export const uploadFileToS3 =
    (uploadUrl: string, file: File, dealerId: number, metadata: DealerLogoMetadata, bffEndpointConfig: IConfigObj) =>
    async (dispatch: any): Promise<void> => {
        try {
            const response = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type }
            });
            if (response.ok) {
                dispatch(confirmLogoUpload(dealerId, metadata, bffEndpointConfig));
            } else {
                dispatch(confirmLogoUploadFailure(`S3 upload failed: ${response.status}`));
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'S3 upload failed';
            dispatch(confirmLogoUploadFailure(message));
        }
    };
export const deletePrintingLogo = (dealerId: number, bffEndpointConfig: IConfigObj) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const endpoint = `${proxyUrl}/printing/logo/${encodeURIComponent(dealerId)}`;
    return {
        [RSAA]: {
            endpoint,
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            types: [DELETE_PRINTING_LOGO_REQUEST, DELETE_PRINTING_LOGO_SUCCESS, DELETE_PRINTING_LOGO_FAILURE]
        } satisfies RSAACallRequest
    };
};
export const fetchPrintingLogo = (dealerId: number, bffEndpointConfig: IConfigObj) => {
    const proxyUrl = buildFromConfig(bffEndpointConfig, '');
    const endpoint = `${proxyUrl}/printing/logo/${encodeURIComponent(dealerId)}`;
    return {
        [RSAA]: {
            endpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [FETCH_PRINTING_LOGO_REQUEST, FETCH_PRINTING_LOGO_SUCCESS, FETCH_PRINTING_LOGO_FAILURE]
        } satisfies RSAACallRequest
    };
};
